<template>
  <div class="property-field">
    <div class="field-container">
      <div class="tw-mb-2" v-if="!isFieldContainer && !property.anyOf">
        <p
          v-if="displayTitle"
          class="field-name"
        >
          {{ displayTitle }}
        </p>
        <!-- <p
          v-if="property.description && !isFieldContainer"
          class="field-description"
        >
          {{ property.description }}
        </p> -->
        <p v-if="!canBeEmpty" class="field-required">
          *Required
        </p>
      </div>

      <div class="field-main">
        <DossierInput
          v-if="property.anyOf"
          :options="property.anyOf"
          :ids="ids"
          :array-index="arrayIndex"
          :display-title="displayTitle"
          @input-changed="handleInputChange"
        />
        <div v-if="fieldMetadata" class="field-metadata">
          <TruncatedText :text="fieldMetadataText" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DossierInput from './DossierInput.vue';
import { useDossierStore } from '../../../stores/dossierStore';
import { TruncatedText } from '@stakeui';

const dossierStore = useDossierStore();

const props = defineProps({
  id: String,
  ids: Array,
  property: Object,
  arrayIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['field-changed']);

const displayTitle = computed(() => {
  return props.property?.title || props.id;
});

const canBeEmpty = computed(() => {
  return props.property.anyOf && props.property.anyOf.some(option => option.type === 'null');
});

// Check if this field is a container that will render its own title/description
const isFieldContainer = computed(() => {
  // If the field has nested objects or arrays, it's a container
  return props.property.anyOf && props.property.anyOf.some(option => 
    option.type === 'object' || 
    option.type === 'array' || 
    option.properties || 
    option.items,
  );
});

const formatSource = src => {
  if (!src) return { classifications: '', file: '' }
  const [rawList, ...rest] = src.split(':')
  const file = rest.join(':').trim()
  const classifications = rawList.replace(/[\[\]']/g, '').trim()
  return { classifications, file }
}



const normalizedPath = computed(() => {
  // 1) build the raw array
  const raw = [...props.ids, props.id];
  console.log('NORMALIZED PATH RAW:', raw)

  // in case it's an array, get the array index (like assets, insurance, etc.)
  const array_index = props.arrayIndex 
  console.log('ARRAY INDEX?',array_index)

  // 2) remove any consecutive duplicate segments
  const deduped = raw.filter((seg, i) => i === 0 || seg !== raw[i - 1]);
  console.log('DEDUPED', deduped)

  // 3) turn numbers into “[i]”, join, and clean up
  return deduped
    .map(segment => isNaN(segment) ? segment : `[${segment}]`)
    .join('.')
    .replace(/\.\[/g, '[')
    .replace(/\]\./g, '].');
});

// Get the metadata for this field
const fieldMetadata = computed(() => {
  if (!dossierStore.currentValue?.metadata?.field_sources) {
    console.log('FIELD SOURCES NOT IN METADATA RETURN NULL FOR SOURCE')
    return null;
  }
  
  // Try to find metadata with exact normalized path
  const sources = dossierStore.currentValue.metadata.field_sources;
  console.log('SOURCES HERE:', sources)

  console.log('normalized path', normalizedPath)
  const testt = normalizedPath.value
  console.log('normalized path value', testt)
  if (sources[normalizedPath.value]) {
    const sourceval = sources[normalizedPath.value]
    console.log('source from normalized path', sourceval)
    return sources[normalizedPath.value];
  }

  // ── if not found, might be an array: try injecting props.arrayIndex after 1st and 2nd segment
  // TO DO -- This is a very hack-y fix. Really this should be taken care of in the normalizedPath computed property, but that doesn't seem to work for array items
  if (typeof props.arrayIndex === 'number' && props.ids.length > 0) {
    const idx = props.arrayIndex;
    // only keep the string segments
    const segments = props.ids.filter(seg => isNaN(seg)).slice(0, -1);    // ← drop the last one
    //const segments = props.ids.filter(seg => isNaN(seg));
    const attempts = [];

    // 1) inject after first segment
    if (segments.length >= 1) {
      const s1 = [...segments];
      s1[0] = `${s1[0]}[${idx}]`;
      attempts.push([...s1, props.id].join('.'));
    }

    // 2) inject after second segment
    if (segments.length >= 2) {
      const s2 = [...segments];
      s2[1] = `${s2[1]}[${idx}]`;
      attempts.push([...s2, props.id].join('.'));
    }
    const test = "insurance.auto_insurance[0].deductible"
    const test2 = sources[test]
    console.log('BIG TEST', test2)
    
    for (const p of attempts) {
      console.log('trying array‐path', p);
      if (sources[p]) {
        console.log('found via array‐path', p);
        return sources[p];
      }
    }
  }
  // If not found, try with just the first two path segments (usually section and field name)
  if (props.ids.length >= 1 && props.id) {
    console.log('TRYING TO FIND SOURCE ANOTHER WAY')
    const proptest = props
    console.log('PROPS', proptest)
    const pathSegments = props.ids.filter(segment => isNaN(segment));
    const simplifiedPath = [
      pathSegments[0],
      props.id,
    ].filter(Boolean).join('.');
    console.log('simplified path', simplifiedPath)
    if (sources[simplifiedPath]) {
      return sources[simplifiedPath];
    }
  }
  console.log('RETURNING NULL FOR SOURCE')
  return null;
});

const fieldMetadataText = computed(() => {
  const formattedSource = formatSource(fieldMetadata.value.source);

  let text = formattedSource.classifications;

  if (formattedSource.file) {
    text += `: ${formattedSource.file}`;
  }

  if (fieldMetadata.value.timestamp) {
    text += `(last updated formatDate(fieldMetadata.timestamp))`;
  }

  return text;
});

// Format date function
const formatDate = timestamp => {
  if (!timestamp) return '';
  
  // If timestamp is a string in ISO format, convert to Date
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  
  return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
};

function handleInputChange() {
  // Set edit mode when field value changes
  dossierStore.setEditMode(true);
  emit('field-changed');
}
</script>

<style scoped>
.property-field {
  width: 100%;
}

.field-container {
  display: flex;
  flex-direction: column;
  border-radius: 0.25rem;
}

.field-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--stk-text-60);
  margin: 0;
  line-height: 1.2;
}

.field-description {
  font-size: 0.75rem;
  color: var(--stk-text-70);
  margin: 0.25rem 0 0 0;
  line-height: 1.2;
}

.field-required {
  font-size: 0.75rem;
  color: var(--stk-danger);
  margin: 0.25rem 0 0 0;
}

.field-main {
  width: 100%;
}

.field-metadata {
  color: var(--stk-text-weak);
  font-size: 0.625em;
  margin-top: 0.25em;
}

.source-info {
  cursor: default;
}
</style>
